import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import LdsButton from "../../components/LdsButton/index.jsx";
import { SideBar } from "../../components/SideBar";
import { useSelector } from "react-redux";

import { ReactLoaderSpinner } from "../../components/ReactLoaderSpinner/index.js";

// services
import { useBookmarkModuleMutation } from "../../store/services/moduleServerApi";

import LdsTile from "../../components/LdsTile/index.jsx";
import LdsImage from "../../components/LdsImage/index.jsx";
import References from "../../components/References/index.jsx";
import TeachBack from "../../assets/module-5/teach-back-method.svg";
const Module_5_1_0 = () => {
  const { pathname: pathName } = useLocation();
  const { menuItems } = useSelector((state) => state?.moduleSlice);

  const [bookmarkModule, { isLoading: isMenuLoading }] =
    useBookmarkModuleMutation();

  /* Start useEffect */
  useEffect(() => {
    bookmarkModule({ pathName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // * onPageLoad
  /* End useEffect */

  return (
    <div className="row">
      {isMenuLoading && <ReactLoaderSpinner />}
      {menuItems?.id && <SideBar menuItems={[menuItems]} />}
      <div className="col-sm-8">
        <main className="module-content">
          <section className="mb-5">
            <h1 className="blue text-uppercase b mt-5">
              5.1.0 Recommended practices on sharing <br />
              results back to patients<sup>1-3</sup>
            </h1>
            <p className="mt-3 mb-0">Estimated time to complete: 2 minutes</p>
          </section>
          <section className="mb-5">
            <h5 className="h5">
              <span className="b">
                This section offers recommended practices for sharing scan
                results with patients.
              </span>
              <sup>1,2</sup>
            </h5>
            <h5 className="h5 b mt-4">
              Discuss scan findings, in addition to clinical findings, potential
              diagnosis, and next steps for the patient
            </h5>

            <LdsTile className="orange-border-tile mt-4 mb-4">
              <p>
                Results of amyloid PET may address specific clinical symptoms of
                MCI or dementia and allow better planning for patients and
                caregivers<sup>1</sup>
              </p>
            </LdsTile>
            <LdsTile className="orange-border-tile mt-3 mb-3">
              <p>
                <strong>
                  A positive Amyvid scan indicates moderate to frequent amyloid
                  neuritic plaques;
                </strong>{" "}
                neuropathological examination has shown this amount of amyloid
                neuritic plaque is present in patients with AD, but may also be
                present in patients with other types of neurologic conditions as
                well as older people with normal cognition<sup>2</sup>
              </p>
            </LdsTile>
            <LdsTile className="orange-border-tile mt-3 mb-3">
              <p>
                <strong>
                  A negative Amyvid scan indicates sparse to no neuritic plaques
                  and is inconsistent
                </strong>{" "}
                with a neuropathological diagnosis of AD at the time of image
                acquisition; a negative scan result reduces the likelihood that
                a patient’s cognitive impairment is due to AD<sup>2</sup>
              </p>
            </LdsTile>
            <LdsTile className="orange-border-tile mt-3 mb-3">
              <p>
                If results are negative, consider further testing to identify
                other possible causes or diagnoses for cognitive impairment
                <sup>1</sup>
              </p>
            </LdsTile>
            <small className="fz-sm">
              <small className="fz-sm">
                Note: Amyvid reports should be used in conjunction with
                cognitive testing and clinical evaluation.
              </small>
            </small>
          </section>

          <section className="row align-items-center">
            <div className="col-sm-12 col-md-6">
              <div className="content-block callout mb-5 mt-3">
                <h3 className="h6 bold">
                  Use the Teach-Back Method<sup>3</sup>
                </h3>
                <ul className="ms-0">
                  <li>
                    The teach-back method is a technique that can be helpful for
                    verifying patients' understanding of their scan results
                  </li>
                  <li>
                    This method has been recommended by the Agency for
                    Healthcare Research and Quality and the Institute for
                    Healthcare Improvement as a strategy for improving health
                    literacy
                  </li>
                  <li>
                    Assessing patient understanding of their test results
                    improves comprehension, clarity, and recall
                  </li>
                  <li>
                    Reminder: Use plain language when asking patients to repeat
                    the information back to you about their amyloid PET scan
                    results
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-sm-12 col-md-6">
              <LdsImage
                src={TeachBack}
                alt="A circular diagram illustrating the 'Teach-Back Process.' The circle is divided into five labeled segments: 'Share,' 'Ask,' 'Listen,' 'Share Again,' and 'Ask Again,' representing the cyclical nature of the process. The central text reads 'Teach-Back Process,' emphasizing the iterative communication flow."
              />
            </div>
          </section>

          <section className="button-control">
            <LdsButton
              classes="white-button mt-3 mb-5 prev"
              onClick={function navClick() {
                window.open("/module-5/module-5-0-5", "_self");
              }}
            >
              Previous Lesson
            </LdsButton>
            <LdsButton
              classes="blue-button mt-3 mb-5 align-self-right float-end next"
              onClick={function navClick() {
                window.open("/module-5/module-5-1-1", "_self");
              }}
            >
              Next Lesson
            </LdsButton>
          </section>
          <References>
            <ol className="no-indent fz-sm">
              <li>
                Rabinovici GD, Knopman DS, Arbizu J, et al. Updated appropriate
                use criteria for amyloid and tau PET.{" "}
                <em>Alzheimer's Dement.</em> 2023;19(S14).
              </li>
              <li>
                Amyvid (florbetapir F 18 injection) Prescribing Information.
                Lilly USA, LLC.
              </li>
              <li>
                Yen PH, Leasure AR. Use and effectiveness of the teach-back
                method in patient education and health outcomes.{" "}
                <em>Fed Pract.</em> 2019;36(6):284-289.
              </li>
            </ol>
          </References>
        </main>
      </div>
    </div>
  );
};

export default Module_5_1_0;
